/**
 * Parse the bulk-patch XLSX template into BulkPatchInputRow objects.
 *
 * Reads the "Data Input" tab from the uploaded file, maps columns to
 * BE field names, and drops rows where the required fields
 * (grobotic_org_pk, product_pk) are missing — those would just be
 * blocked at the BE anyway, and silently ignoring them keeps the
 * preview cleaner. (Users who fill 100 rows but only use 67 don't
 * see 33 spurious "blocked: missing fields" entries.)
 *
 * If the user uploads a file with the wrong sheet name (e.g. they
 * renamed it), falls back to the first sheet and surfaces a
 * non-fatal warning so they know to check.
 */

import { read as xlsxRead, utils as xlsxUtils } from 'xlsx'
import type { BulkPatchInputRow } from 'api/aws/deals/useBulkPatch'

const TARGET_SHEET = 'Data Input'

export interface ParseResult {
  rows: BulkPatchInputRow[]
  /** Rows present in the file but dropped from the parsed output
   *  because they had no required-field data (the user hadn't filled
   *  them yet). Just a count — for "20 rows in template, 7 filled out" UX. */
  empty_rows_dropped: number
  /** Non-fatal warnings (e.g. unknown columns, fallback to first sheet). */
  warnings: string[]
}

export async function parseTemplateXlsx(file: File): Promise<ParseResult> {
  const buf = await file.arrayBuffer()
  const wb = xlsxRead(buf, { type: 'array' })

  const warnings: string[] = []

  // Locate the data sheet — prefer "Data Input", fall back to first sheet
  let sheetName = TARGET_SHEET
  if (!wb.Sheets[sheetName]) {
    const fallback = wb.SheetNames[wb.SheetNames.length - 1]
    warnings.push(
      `Sheet "${TARGET_SHEET}" not found — using "${fallback}" instead. ` +
        `If that's not what you meant, re-download the template.`,
    )
    sheetName = fallback
  }
  const sheet = wb.Sheets[sheetName]

  // Get raw rows as objects keyed by header
  const raw = xlsxUtils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: null, // empty cells → null (not undefined) so they're consistent
    raw: true,
  })

  const rows: BulkPatchInputRow[] = []
  let emptyRowsDropped = 0

  for (const r of raw) {
    const orgPK = stringField(r, 'grobotic_org_pk')
    const productPK = stringField(r, 'product_pk')

    // Required fields missing → assume user hasn't filled this row, skip
    if (!orgPK && !productPK) {
      emptyRowsDropped++
      continue
    }

    // Either field missing (but not both) → still pass through so BE
    // surfaces a clear block reason. Better than silently dropping.
    rows.push({
      row_index: numberField(r, 'row_index') ?? undefined,
      grobotic_org_pk: orgPK ?? '',
      deal_uuid: stringField(r, 'deal_uuid') ?? undefined,
      product_pk: productPK ?? '',
      product_name: stringField(r, 'product_name') ?? undefined,
      new_price: numberField(r, 'new_price') ?? null,
      new_margin: numberField(r, 'new_margin') ?? null,
      new_start_date: dateField(r, 'new_start_date') ?? null,
      new_end_date: dateField(r, 'new_end_date') ?? null,
    })
  }

  return { rows, empty_rows_dropped: emptyRowsDropped, warnings }
}

// ── Field coercers ────────────────────────────────────────────────────

function stringField(r: Record<string, unknown>, key: string): string | null {
  const v = r[key]
  if (v == null) return null
  const s = String(v).trim()
  return s.length > 0 ? s : null
}

function numberField(r: Record<string, unknown>, key: string): number | null {
  const v = r[key]
  if (v == null || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const n = parseFloat(String(v))
  return Number.isFinite(n) ? n : null
}

/**
 * Coerce a cell to YYYY-MM-DD. Handles three shapes:
 *   - Excel date serial number (e.g. 46571 = 2027-06-10)
 *   - JS Date object (some xlsx parser configs return these)
 *   - String already in YYYY-MM-DD or similar
 */
function dateField(r: Record<string, unknown>, key: string): string | null {
  const v = r[key]
  if (v == null || v === '') return null

  // Excel serial number — days since 1900-01-01 (with the famous leap-year bug)
  if (typeof v === 'number' && v > 0) {
    // xlsx ships a helper, but inline math keeps this dep-free for the helper.
    // Excel epoch: 1900-01-01 = 1, but Excel mistakenly treats 1900 as leap year
    // so we subtract 2 (one for 0-indexing, one for the phantom Feb 29).
    const ms = (v - 25569) * 86400 * 1000
    const d = new Date(ms)
    return d.toISOString().slice(0, 10)
  }

  if (v instanceof Date) {
    return v.toISOString().slice(0, 10)
  }

  // String — try to parse to a Date, fall back to YYYY-MM-DD slice if it
  // already looks like the right format.
  const s = String(v).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const parsed = new Date(s)
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10)
  return null
}
